import { type FC, Fragment, useState } from "react";

import { DownloadLink } from "src/components";
import { CheckIcon, CopyIcon } from "src/icons";
import { useTranslation } from "src/translations";
import type { PayoutPaymentGatewayUri } from "src/types";
import { isNotNullish } from "src/utils";

type PayoutTransferDetailsProps = Readonly<{
    details: PayoutPaymentGatewayUri;
}>;

export const PayoutTransferDetails: FC<PayoutTransferDetailsProps> = ({ details }) => {
    const { t } = useTranslation();
    const [copiedLabel, setCopiedLabel] = useState<string | null>(null);

    const rows = [
        {
            label: t("orders.pay.transferDetails.amount"),
            value: isNotNullish(details.amount)
                ? t("format.currency", { value: parseFloat(details.amount), currency: details.currency })
                : undefined,
        },
        { label: t("orders.pay.transferDetails.accountNumber"), value: details.iban },
        { label: t("orders.pay.transferDetails.variableSymbol"), value: details.variable_symbol },
    ].filter((row) => isNotNullish(row.value));

    const handleCopy = (row: (typeof rows)[number]) => {
        if (!isNotNullish(row.value)) return;

        navigator.clipboard.writeText(row.value);
        setCopiedLabel(row.label);
        setTimeout(() => setCopiedLabel((current) => (current === row.label ? null : current)), 1500);
    };

    return (
        <div className="grid gap-4">
            {rows.length > 0 && (
                <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                    {rows.map((row) => (
                        <Fragment key={row.label}>
                            <div>{row.label}</div>
                            <div className="flex items-center justify-end gap-1.5 text-right font-semibold">
                                <span>{row.value}</span>
                                <button type="button" aria-label={t("orders.pay.transferDetails.copy")} onClick={() => handleCopy(row)}>
                                    {copiedLabel === row.label ? (
                                        <CheckIcon className="size-4 fill-brand" />
                                    ) : (
                                        <CopyIcon className="size-4 text-primary" />
                                    )}
                                </button>
                            </div>
                        </Fragment>
                    ))}
                </div>
            )}
            {details.qr_code && (
                <img
                    src={`data:image/png;base64,${details.qr_code}`}
                    alt={t("orders.pay.transferDetails.qrCode")}
                    className="mx-auto size-40"
                />
            )}
            {details.uri && (
                <DownloadLink href={details.uri} className="mx-auto">
                    {t("orders.pay.transferDetails.goToPaymentGateway")}
                </DownloadLink>
            )}
        </div>
    );
};
